import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import DsButton from '../DsButton';
import DsModal from '../DsModal';
import WebPdfDocumentPreview from './WebPdfDocumentPreview';
import { downloadBlobFromUrl } from '../../../api/storage';
import '../../../css/common/pdf/PdfPreview.css';

type PdfPreviewRequest = { url: string; fileName: string };
export type PdfPreviewInput = { url?: string; fileName?: string; mimeType?: string };
type PdfPreviewContextValue = { openPreview: (_input: PdfPreviewInput) => void };

const PdfPreviewContext = createContext<PdfPreviewContextValue | null>(null);

export const isPdfPreviewable = ({
  fileName = '',
  mimeType = '',
  url = '',
}: PdfPreviewInput = {}): boolean => {
  const normalizedType = String(mimeType || '').toLowerCase();
  return normalizedType.includes('pdf') || /\.pdf(?:$|[?#])/i.test(fileName) || /\.pdf(?:$|[?#])/i.test(url);
};

const ensurePdfFilename = (value = ''): string => {
  const normalized = String(value || '').trim() || 'document.pdf';
  return /\.pdf$/i.test(normalized) ? normalized : `${normalized}.pdf`;
};

export const isPdfBlob = async (blob: unknown): Promise<boolean> => {
  if (!(blob instanceof Blob)) return false;
  const prefix = await blob.slice(0, 5).text();
  return prefix === '%PDF-';
};

const saveBlob = (blob: Blob, filename?: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = ensurePdfFilename(filename);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};

const canShareFile = (file: File | null): boolean => {
  if (!file || typeof navigator === 'undefined') return false;
  if (typeof navigator.share !== 'function' || typeof navigator.canShare !== 'function') return false;
  try {
    return navigator.canShare({ files: [file] });
  } catch {
    return false;
  }
};

export const PdfPreviewProvider = ({ children }: { children: ReactNode }) => {
  const [request, setRequest] = useState<PdfPreviewRequest | null>(null);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const loadSequenceRef = useRef(0);

  const closePreview = useCallback(() => {
    loadSequenceRef.current += 1;
    setRequest(null);
    setLoading(false);
    setError('');
    setPdfBlob(null);
  }, []);

  const openPreview = useCallback(({ url, fileName }: PdfPreviewInput = {}) => {
    if (!url) return;
    setRequest({ url, fileName: ensurePdfFilename(fileName) });
  }, []);

  const loadPdf = useCallback(async () => {
    if (!request?.url) return;
    const sequence = loadSequenceRef.current + 1;
    loadSequenceRef.current = sequence;
    setLoading(true);
    setError('');
    setPdfBlob(null);
    try {
      const downloaded = await downloadBlobFromUrl(request.url, 'include');
      if (!(await isPdfBlob(downloaded))) {
        throw new Error('PDF形式のファイルとして確認できませんでした。');
      }
      if (loadSequenceRef.current !== sequence) return;
      const typedBlob = downloaded.type === 'application/pdf'
        ? downloaded
        : new Blob([downloaded], { type: 'application/pdf' });
      setPdfBlob(typedBlob);
    } catch (loadError: unknown) {
      if (loadSequenceRef.current !== sequence) return;
      setError(loadError instanceof Error ? loadError.message : 'PDFを読み込めませんでした。');
    } finally {
      if (loadSequenceRef.current === sequence) setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    if (request?.url) loadPdf();
  }, [loadPdf, request?.url]);

  useEffect(() => () => {
    loadSequenceRef.current += 1;
  }, []);

  const shareFile = useMemo(() => {
    if (!pdfBlob || typeof File !== 'function') return null;
    return new File([pdfBlob], request?.fileName || 'document.pdf', { type: 'application/pdf' });
  }, [pdfBlob, request?.fileName]);
  const canShare = canShareFile(shareFile);

  const handleShare = useCallback(async () => {
    if (!canShare || !shareFile) return;
    try {
      await navigator.share({ files: [shareFile], title: request?.fileName || 'PDF' });
    } catch (shareError: unknown) {
      if (!(shareError instanceof DOMException && shareError.name === 'AbortError')) {
        setError(shareError instanceof Error ? shareError.message : 'PDFを共有できませんでした。');
      }
    }
  }, [canShare, request?.fileName, shareFile]);

  const contextValue = useMemo(() => ({ openPreview }), [openPreview]);
  const closeAction = (
    <DsButton
      variant="secondary"
      size="sm"
      icon
      aria-label="PDFプレビューを閉じる"
      title="閉じる"
      onClick={closePreview}
    >
      <i className="fa-solid fa-xmark" aria-hidden="true" />
    </DsButton>
  );
  const fileActions = pdfBlob ? (
    <>
      {canShare ? (
        <DsButton
          variant="secondary"
          size="sm"
          icon
          aria-label="共有"
          title="共有"
          onClick={handleShare}
        >
          <i className="fa-solid fa-share-nodes" aria-hidden="true" />
        </DsButton>
      ) : null}
      <DsButton
        variant="primary"
        size="sm"
        icon
        aria-label="保存"
        title="保存"
        onClick={() => saveBlob(pdfBlob, request?.fileName)}
      >
        <i className="fa-solid fa-download" aria-hidden="true" />
      </DsButton>
      {closeAction}
    </>
  ) : closeAction;

  return (
    <PdfPreviewContext.Provider value={contextValue}>
      {children}
      <DsModal
        open={Boolean(request)}
        onClose={closePreview}
        title={undefined}
        ariaLabel={`${request?.fileName || 'PDF'}のプレビュー`}
        footer={undefined}
        showClose={false}
        size="lg"
        overlayClassName="pdf-preview-overlay"
        className="pdf-preview"
        bodyClassName="pdf-preview__body"
      >
        {loading ? (
          <div className="pdf-preview__state-shell">
            <div className="pdf-preview__state-toolbar">
              <span className="pdf-document-preview__filename" title={request?.fileName}>{request?.fileName}</span>
              <div className="pdf-document-preview__actions">{closeAction}</div>
            </div>
            <div className="pdf-preview__state" role="status">PDFを読み込んでいます...</div>
          </div>
        ) : error ? (
          <div className="pdf-preview__state-shell">
            <div className="pdf-preview__state-toolbar">
              <span className="pdf-document-preview__filename" title={request?.fileName}>{request?.fileName}</span>
              <div className="pdf-document-preview__actions">{closeAction}</div>
            </div>
            <div className="pdf-preview__state pdf-preview__state--error" role="alert">
              <p>{error}</p>
              <DsButton variant="secondary" onClick={loadPdf}>再試行</DsButton>
            </div>
          </div>
        ) : pdfBlob ? (
          <WebPdfDocumentPreview
            blob={pdfBlob}
            fileName={request?.fileName || 'document.pdf'}
            onError={setError}
            toolbarActions={fileActions}
          />
        ) : null}
      </DsModal>
    </PdfPreviewContext.Provider>
  );
};

export const usePdfPreview = () => useContext(PdfPreviewContext);
